import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../sidebar/TopBar";
import { END_POINT } from "../../../helper/helper";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const emptyToolForm = {
  name: "", company: "", category: "", description: "",
  pricing: "Freemium", price: "", rating: 0, reviews: 0,
  icon: "", website: "", tagsCsv: "", featuresCsv: "",
  usecaseCsv: "", platformCsv: "", badgesCsv: "",
  youtube: "", tutorial: "", demo: "", releaseDate: "",
  featureItems: [{ text: "", videoUrl: "" }],
};

const csvToArray = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);
const normalizeFeatureItems = (features) => {
  if (!Array.isArray(features)) return [{ text: "", videoUrl: "" }];
  const mapped = features.map((item) => {
    if (typeof item === "string") return { text: item, videoUrl: "" };
    return {
      text: (item?.text || item?.title || "").trim(),
      videoUrl: (item?.videoUrl || item?.video || "").trim(),
    };
  });
  const valid = mapped.filter((f) => f.text || f.videoUrl);
  return valid.length ? valid : [{ text: "", videoUrl: "" }];
};

const toolToForm = (tool) => ({
  name: tool.name || "", company: tool.company || "", category: tool.category || "",
  description: tool.description || "", pricing: tool.pricing || "Freemium",
  price: tool.price || "", rating: tool.rating || 0, reviews: tool.reviews || 0,
  icon: tool.icon || "", website: tool.website || "",
  tagsCsv: (tool.tags || []).join(", "),
  featuresCsv: (tool.features || []).join(", "),
  usecaseCsv: (tool.usecase || []).join(", "),
  platformCsv: (tool.platform || []).join(", "),
  badgesCsv: (tool.badges || []).join(", "),
  youtube: tool.videos?.youtube || "", tutorial: tool.videos?.tutorial || "",
  demo: tool.videos?.demo || "", releaseDate: tool.newFeatures?.releaseDate || "",
  featureItems: normalizeFeatureItems(tool.newFeatures?.features),
});

const formToTool = (form) => ({
  name: form.name.trim(), company: form.company.trim(), category: form.category.trim(),
  description: form.description.trim(), pricing: form.pricing,
  price: form.price.trim(), rating: Number(form.rating) || 0,
  reviews: Number(form.reviews) || 0, icon: form.icon.trim(),
  tags: csvToArray(form.tagsCsv), features: csvToArray(form.featuresCsv),
  usecase: csvToArray(form.usecaseCsv), platform: csvToArray(form.platformCsv),
  badges: csvToArray(form.badgesCsv), website: form.website.trim(),
  videos: { youtube: form.youtube.trim(), tutorial: form.tutorial.trim(), demo: form.demo.trim() },
  newFeatures: form.releaseDate.trim() || (form.featureItems || []).some((f) => f.text.trim() || f.videoUrl.trim())
    ? {
      releaseDate: form.releaseDate.trim(),
      features: (form.featureItems || [])
        .map((f) => ({ text: f.text.trim(), videoUrl: f.videoUrl.trim() }))
        .filter((f) => f.text),
    }
    : null,
});

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const c = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E8ECF4",
  text: "#1A202C",
  textMuted: "#718096",
  textFaint: "#A0AEC0",
  accent: "#4F46E5",
  accentLight: "#EEF2FF",
  accentBorder: "#C7D2FE",
  success: "#059669",
  successLight: "#D1FAE5",
  danger: "#DC2626",
  dangerLight: "#FEE2E2",
  warning: "#D97706",
};

const inputBase = {
  width: "100%", padding: "9px 12px", fontSize: "13px",
  border: `1px solid ${c.border}`, borderRadius: "8px",
  background: c.bg, color: c.text, outline: "none",
  fontFamily: "'Inter', system-ui, sans-serif",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

// ─── MICRO COMPONENTS ─────────────────────────────────────────────────────────
const Field = ({ label, required, children, half }) => (
  <div style={{ marginBottom: "14px", gridColumn: half ? "span 1" : "span 2" }}>
    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: c.textMuted, marginBottom: "5px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
      {label}{required && <span style={{ color: c.accent }}> *</span>}
    </label>
    {children}
  </div>
);

const FocusInput = ({ as: Tag = "input", ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <Tag
      {...props}
      style={{
        ...inputBase,
        ...(Tag === "textarea" ? { resize: "vertical", lineHeight: "1.5" } : {}),
        ...(Tag === "select" ? {
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23718096' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
          paddingRight: "32px", cursor: "pointer",
        } : {}),
        borderColor: focused ? c.accent : c.border,
        boxShadow: focused ? `0 0 0 3px ${c.accentLight}` : "none",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Btn = ({ variant = "primary", size = "md", children, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const sizes = { sm: { padding: "6px 12px", fontSize: "12px" }, md: { padding: "10px 18px", fontSize: "13px" }, lg: { padding: "11px 22px", fontSize: "14px" } };
  const variants = {
    primary: { background: hovered && !props.disabled ? "#4338CA" : c.accent, color: "#fff", border: "none", opacity: props.disabled ? 0.6 : 1 },
    ghost: { background: hovered ? c.border : "transparent", color: c.textMuted, border: `1px solid ${c.border}` },
    danger: { background: hovered ? "#FEE2E2" : "#FFF5F5", color: c.danger, border: `1px solid #FECACA` },
    edit: { background: hovered ? c.accentLight : "#F1F5FF", color: c.accent, border: `1px solid ${c.accentBorder}` },
    outline: { background: hovered ? c.accentLight : c.surface, color: c.accent, border: `1px solid ${c.accentBorder}` },
  };
  return (
    <button
      {...props}
      style={{
        ...sizes[size], ...variants[variant],
        borderRadius: "8px", cursor: props.disabled ? "not-allowed" : "pointer",
        fontWeight: 600, fontFamily: "'Inter', system-ui, sans-serif",
        transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: "6px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, color = c.accent }) => (
  <span style={{
    display: "inline-block", padding: "2px 8px", borderRadius: "20px",
    fontSize: "10px", fontWeight: 600, letterSpacing: "0.04em",
    background: color + "18", color, border: `1px solid ${color}30`,
  }}>{children}</span>
);

const pricingColor = (p) => p === "Free" ? c.success : p === "Freemium" ? c.warning : c.accent;

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "20px 0 16px", gridColumn: "span 2" }}>
    <div style={{ height: "1px", flex: 1, background: c.border }} />
    <span style={{ fontSize: "10px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{label}</span>
    <div style={{ height: "1px", flex: 1, background: c.border }} />
  </div>
);

const Toast = ({ message }) => {
  const colors = {
    success: { bg: c.successLight, color: c.success, border: "#A7F3D0", icon: "✓" },
    error: { bg: c.dangerLight, color: c.danger, border: "#FECACA", icon: "✕" },
    info: { bg: c.accentLight, color: c.accent, border: c.accentBorder, icon: "ℹ" },
  };
  if (!message.text) return null;
  const s = colors[message.type] || colors.info;
  return (
    <div style={{
      position: "fixed", top: "20px", right: "24px", zIndex: 999,
      padding: "12px 18px", borderRadius: "10px", fontSize: "13px", fontWeight: 500,
      display: "flex", alignItems: "center", gap: "8px",
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      animation: "slideIn 0.2s ease",
    }}>
      <span style={{ fontWeight: 700 }}>{s.icon}</span> {message.text}
    </div>
  );
};

// ─── PAGE: TOOLS LIST ─────────────────────────────────────────────────────────
const ToolsListPage = ({ tools, categories, onAdd, onEdit, onDelete, saving }) => {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterPricing, setFilterPricing] = useState("all");

  const filtered = tools.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.name?.toLowerCase().includes(q) || t.company?.toLowerCase().includes(q);
    const matchCat = filterCat === "all" || t.category === filterCat;
    const matchPricing = filterPricing === "all" || t.pricing === filterPricing;
    return matchSearch && matchCat && matchPricing;
  });

  return (
    <div style={{ maxWidth: "1350px", margin: "0 auto", padding: "28px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #4F46E5, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>🤖</div>
            <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: c.text }}>AI Tools Directory</h1>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: c.textMuted, paddingLeft: "46px" }}>
            {tools.length} tools across {categories.length} categories
          </p>
        </div>
        <Btn size="lg" onClick={onAdd}>
          <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span> Add New Tool
        </Btn>
      </div>

      {/* Filters */}
      <div style={{
        background: c.surface, border: `1px solid ${c.border}`, borderRadius: "12px",
        padding: "14px 16px", marginBottom: "16px",
        display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap",
      }}>
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: c.textFaint, fontSize: "14px" }}>🔍</span>
          <input
            placeholder="Search tools…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputBase, paddingLeft: "32px", fontSize: "12px" }}
          />
        </div>
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          style={{ ...inputBase, width: "auto", minWidth: "160px", appearance: "none", paddingRight: "28px", fontSize: "12px", cursor: "pointer", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23718096' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
        </select>
        <select
          value={filterPricing}
          onChange={e => setFilterPricing(e.target.value)}
          style={{ ...inputBase, width: "auto", minWidth: "130px", appearance: "none", paddingRight: "28px", fontSize: "12px", cursor: "pointer", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23718096' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
        >
          <option value="all">All Pricing</option>
          <option>Free</option>
          <option>Freemium</option>
          <option>Paid</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: "12px", color: c.textMuted }}>
          Showing <strong style={{ color: c.text }}>{filtered.length}</strong> of {tools.length}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 24px", color: c.textFaint }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔍</div>
          <div style={{ fontSize: "15px", fontWeight: 600, color: c.textMuted, marginBottom: "6px" }}>No tools found</div>
          <div style={{ fontSize: "13px" }}>Try adjusting your filters or add a new tool.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
          {filtered.map((tool) => {
            const realIndex = tools.indexOf(tool);
            return (
              <div
                key={`${tool.name}-${realIndex}`}
                style={{
                  background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px",
                  padding: "16px", display: "flex", flexDirection: "column", gap: "10px",
                  transition: "box-shadow 0.15s, border-color 0.15s", cursor: "default",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(79,70,229,0.1)"; e.currentTarget.style.borderColor = c.accentBorder; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = c.border; }}
              >
                {/* Card top */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                    background: "linear-gradient(135deg, #f0f4ff, #e0e7ff)",
                    border: `1px solid ${c.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px",
                  }}>{tool.icon || "🤖"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: "14px", color: c.text, marginBottom: "4px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      {tool.name}
                      {tool.pricing && <Badge color={pricingColor(tool.pricing)}>{tool.pricing}</Badge>}
                    </div>
                    <div style={{ fontSize: "12px", color: c.textMuted, display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                      {tool.company && <span>{tool.company}</span>}
                      {tool.category && (
                        <>
                          {tool.company && <span>·</span>}
                          <code style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "4px", padding: "1px 6px", fontSize: "10px" }}>{tool.category}</code>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {tool.description && (
                  <p style={{ margin: 0, fontSize: "12px", color: c.textMuted, lineHeight: "1.5", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {tool.description}
                  </p>
                )}

                {/* Meta row */}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {tool.rating > 0 && (
                    <span style={{ fontSize: "11px", color: c.warning, fontWeight: 600 }}>★ {Number(tool.rating).toFixed(1)}</span>
                  )}
                  {tool.reviews > 0 && (
                    <span style={{ fontSize: "11px", color: c.textFaint }}>{tool.reviews.toLocaleString()} reviews</span>
                  )}
                  {tool.price && (
                    <span style={{ fontSize: "11px", color: c.textMuted, marginLeft: "auto" }}>{tool.price}</span>
                  )}
                </div>

                {/* Tags */}
                {tool.tags?.length > 0 && (
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {tool.tags.slice(0, 4).map((tag, i) => (
                      <span key={i} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "4px", background: c.bg, border: `1px solid ${c.border}`, color: c.textMuted }}>
                        {tag}
                      </span>
                    ))}
                    {tool.tags.length > 4 && <span style={{ fontSize: "10px", color: c.textFaint, padding: "2px 4px" }}>+{tool.tags.length - 4}</span>}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", gap: "6px", marginTop: "auto", paddingTop: "4px", borderTop: `1px solid ${c.border}` }}>
                  <Btn size="sm" variant="edit" type="button" onClick={() => onEdit(realIndex)} style={{ flex: 1, justifyContent: "center" }}>✏️ Edit</Btn>
                  <Btn size="sm" variant="danger" type="button" onClick={() => onDelete(realIndex)}>🗑️</Btn>
                  {tool.website && (
                    <a href={tool.website} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                      <Btn size="sm" variant="ghost" type="button">↗</Btn>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── PAGE: TOOL FORM ──────────────────────────────────────────────────────────
const ToolFormPage = ({ toolForm, setToolForm, onSubmit, onCancel, saving, isEdit, categories }) => {
  const tf = toolForm;
  const set = (key) => (e) => setToolForm({ ...tf, [key]: e.target.value });
  const updateFeatureItem = (index, key, value) => {
    const nextItems = [...(tf.featureItems || [])];
    nextItems[index] = { ...nextItems[index], [key]: value };
    setToolForm({ ...tf, featureItems: nextItems });
  };
  const addFeatureItem = () => {
    setToolForm({
      ...tf,
      featureItems: [...(tf.featureItems || []), { text: "", videoUrl: "" }],
    });
  };
  const removeFeatureItem = (index) => {
    const nextItems = (tf.featureItems || []).filter((_, i) => i !== index);
    setToolForm({
      ...tf,
      featureItems: nextItems.length ? nextItems : [{ text: "", videoUrl: "" }],
    });
  };

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "28px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <button
          onClick={onCancel}
          style={{ background: "none", border: "none", cursor: "pointer", color: c.textMuted, fontSize: "20px", lineHeight: 1, padding: "4px", borderRadius: "6px", display: "flex" }}
          title="Back"
        >←</button>
        <div style={{ width: 1, height: "24px", background: c.border }} />
        <div style={{ width: 34, height: 34, borderRadius: "9px", background: "linear-gradient(135deg, #4F46E5, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>🤖</div>
        <div>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: c.text }}>
            {isEdit ? "Edit AI Tool" : "Add New AI Tool"}
          </h1>
          <p style={{ margin: 0, fontSize: "12px", color: c.textMuted }}>
            {isEdit ? `Editing: ${tf.name}` : "Fill in the details below to add a new tool."}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: "16px" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${c.border}`, background: c.bg, fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Basic Information
          </div>
          <div style={{ padding: "20px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Field label="Tool Name" required>
              <FocusInput placeholder="e.g. ChatGPT" value={tf.name} onChange={set("name")} />
            </Field>
            <Field label="Company">
              <FocusInput placeholder="e.g. OpenAI" value={tf.company} onChange={set("company")} />
            </Field>
            <Field label="Category" required>
              <FocusInput as="select" value={tf.category} onChange={set("category")}>
                <option value="">— Select a category —</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </FocusInput>
            </Field>
            <Field label="Icon (emoji or text)">
              <FocusInput placeholder="🤖" value={tf.icon} onChange={set("icon")} />
            </Field>
            <Field label="Description" style={{ gridColumn: "span 2" }}>
              <FocusInput as="textarea" placeholder="Short description of what this tool does…" value={tf.description} onChange={set("description")} rows={3} style={{ gridColumn: "span 2" }} />
            </Field>
          </div>
        </div>

        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: "16px" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${c.border}`, background: c.bg, fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Pricing & Ratings
          </div>
          <div style={{ padding: "20px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Field label="Pricing Model">
              <FocusInput as="select" value={tf.pricing} onChange={set("pricing")}>
                <option>Free</option>
                <option>Freemium</option>
                <option>Paid</option>
              </FocusInput>
            </Field>
            <Field label="Price Text">
              <FocusInput placeholder="e.g. From $20/mo" value={tf.price} onChange={set("price")} />
            </Field>
            <Field label="Rating (0–5)">
              <FocusInput type="number" step="0.1" min="0" max="5" placeholder="0.0" value={tf.rating} onChange={set("rating")} />
            </Field>
            <Field label="Reviews Count">
              <FocusInput type="number" placeholder="0" value={tf.reviews} onChange={set("reviews")} />
            </Field>
          </div>
        </div>

        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: "16px" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${c.border}`, background: c.bg, fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Metadata
          </div>
          <div style={{ padding: "20px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Field label="Tags">
              <FocusInput placeholder="AI, NLP, Writing…" value={tf.tagsCsv} onChange={set("tagsCsv")} />
            </Field>
            <Field label="Features">
              <FocusInput placeholder="Chat, API, Plugins…" value={tf.featuresCsv} onChange={set("featuresCsv")} />
            </Field>
            {/* <Field label="Use Cases">
              <FocusInput placeholder="Customer support, Coding…" value={tf.usecaseCsv} onChange={set("usecaseCsv")} />
            </Field>
            <Field label="Platforms">
              <FocusInput placeholder="Web, iOS, Android…" value={tf.platformCsv} onChange={set("platformCsv")} />
            </Field> */}
            {/* <Field label="Badges">
              <FocusInput placeholder="New, Hot, Staff Pick…" value={tf.badgesCsv} onChange={set("badgesCsv")} />
            </Field> */}
          </div>
        </div>

        {/* <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: "16px" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${c.border}`, background: c.bg, fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Links & Media
          </div>
          <div style={{ padding: "20px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Field label="Website URL">
              <FocusInput placeholder="https://…" value={tf.website} onChange={set("website")} />
            </Field>
            <Field label="YouTube Embed URL">
              <FocusInput placeholder="https://youtube.com/embed/…" value={tf.youtube} onChange={set("youtube")} />
            </Field>
            <Field label="Tutorial URL">
              <FocusInput placeholder="https://…" value={tf.tutorial} onChange={set("tutorial")} />
            </Field>
            <Field label="Demo / Docs URL">
              <FocusInput placeholder="https://…" value={tf.demo} onChange={set("demo")} />
            </Field>
          </div>
        </div> */}

        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: "24px" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${c.border}`, background: c.bg, fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            New Features
          </div>
          <div style={{ padding: "20px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Field label="Release Date">
              <FocusInput placeholder="e.g. 2024-01-15" value={tf.releaseDate} onChange={set("releaseDate")} />
            </Field>
            <Field label="Feature Rows + Video Link">
              <div style={{ display: "grid", gap: "8px" }}>
                {(tf.featureItems || []).map((item, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "8px", alignItems: "center" }}>
                    <FocusInput
                      placeholder="Feature text"
                      value={item.text}
                      onChange={(e) => updateFeatureItem(idx, "text", e.target.value)}
                    />
                    <FocusInput
                      placeholder="Video link (optional)"
                      value={item.videoUrl}
                      onChange={(e) => updateFeatureItem(idx, "videoUrl", e.target.value)}
                    />
                    <Btn type="button" variant="danger" size="sm" onClick={() => removeFeatureItem(idx)}>
                      -
                    </Btn>
                  </div>
                ))}
                <div>
                  <Btn type="button" variant="outline" size="sm" onClick={addFeatureItem}>
                    + Add Feature
                  </Btn>
                </div>
              </div>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", paddingTop: "4px" }}>
          <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
          <Btn type="submit" size="lg" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Tool"}
          </Btn>
        </div>
      </form>
    </div>
  );
};

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const AITool = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [page, setPage] = useState("list"); // "list" | "form"
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [toolForm, setToolForm] = useState(emptyToolForm);
  const [editingToolIndex, setEditingToolIndex] = useState(null);

  const setMsg = (text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(END_POINT.GetHomeDirectoryData);
      const result = await res.json();
      if (result?.success && result?.data) {
        setCategories(Array.isArray(result.data.categories) ? result.data.categories : []);
        setTools(Array.isArray(result.data.aiTools) ? result.data.aiTools : []);
      }
    } catch { setMsg("Failed to load data.", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const persistData = async (nextTools) => {
    setSaving(true);
    try {
      const res = await fetch(END_POINT.UpsertHomeDirectoryData, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categories, aiTools: nextTools }),
      });
      const result = await res.json();
      if (!result?.success) throw new Error();
      setTools(nextTools);
      setMsg("Changes saved successfully.", "success");
      return true;
    } catch { setMsg("Save failed. Please try again.", "error"); return false; }
    finally { setSaving(false); }
  };

  const handleAdd = () => { setToolForm(emptyToolForm); setEditingToolIndex(null); setPage("form"); };
  const handleEdit = (i) => { setToolForm(toolToForm(tools[i])); setEditingToolIndex(i); setPage("form"); };
  const handleCancel = () => { setPage("list"); setEditingToolIndex(null); setToolForm(emptyToolForm); };

  const handleDelete = async (i) => {
    const next = tools.filter((_, idx) => idx !== i);
    await persistData(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toolForm.name.trim() || !toolForm.category.trim()) {
      setMsg("Tool name and category are required.", "error"); return;
    }
    const payload = formToTool(toolForm);
    const next = [...tools];
    if (editingToolIndex === null) next.push(payload);
    else next[editingToolIndex] = payload;
    const ok = await persistData(next);
    if (ok) { setPage("list"); setEditingToolIndex(null); setToolForm(emptyToolForm); }
  };

  return (
    <div style={{ background: c.bg, minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input, textarea, select { font-family: 'Inter', system-ui, sans-serif !important; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 10px; }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <TopBar />
      <Toast message={message} />

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: "12px", color: c.textMuted, fontSize: "14px" }}>
          <div style={{ width: 22, height: 22, border: `2px solid ${c.border}`, borderTopColor: c.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          Loading tools…
        </div>
      ) : page === "list" ? (
        <ToolsListPage
          tools={tools}
          categories={categories}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          saving={saving}
        />
      ) : (
        <ToolFormPage
          toolForm={toolForm}
          setToolForm={setToolForm}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          saving={saving}
          isEdit={editingToolIndex !== null}
          categories={categories}
        />
      )}
    </div>
  );
};

export default AITool;
